/**
 * LARCIN header — the small amount of behaviour CSS and HTML cannot do.
 *
 * What is deliberately NOT here: search, the cart, the cart count, the cart
 * drawer and the account menu. Those are the theme's own components, rendered
 * by snippets/search.liquid and snippets/header-actions.liquid, and this file
 * never touches them.
 *
 * 1. THE MOBILE DRAWER is a native <dialog>. The browser gives the backdrop,
 *    the focus trap and the Escape key; all this adds is opening it, closing
 *    it, and standing out of the way when the viewport grows into the desktop
 *    layout with the drawer still open.
 *
 * 2. THE MEGA MENU opens on hover through CSS alone. This adds the pointerless
 *    half: a toggle button that reports its state, closing on Escape and on a
 *    click elsewhere. Nothing here is required for the links to work — without
 *    this file every one of them is still an ordinary anchor.
 *
 * 3. --header-height, which the theme's own header sets and several sections
 *    read (the hero, the sticky product column, the account popover). The
 *    theme's script sets it from its own <header-component> element, which
 *    this header is not, so it is measured here instead.
 *
 * 4. "ONLY WHEN SCROLLING UP": toggles .lh--away (the CSS slides the header
 *    out) and data-sticky-state between 'active' and 'idle'.
 */
(() => {
  const header = document.getElementById('header-component');
  if (!header) return;

  /* ---------------------------------------------------------- Drawer */
  const drawer = document.getElementById('lh-drawer');
  const openButton = header.querySelector('[data-lh-drawer-open]');

  if (drawer instanceof HTMLDialogElement && openButton) {
    // Putting the page back: the button's state and the scroll lock. Called
    // directly by every close this file performs, and again from the dialog's
    // own events for the closes it does not — Escape and the backdrop.
    // Deliberately not left to the close event alone: that event is queued
    // rather than dispatched synchronously, and a missed one would leave the
    // page locked with no way back.
    const release = () => {
      openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('lh-drawer-open');
    };

    const open = () => {
      if (drawer.open) return;
      drawer.showModal();
      openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('lh-drawer-open');
    };

    const close = () => {
      if (drawer.open) drawer.close();
      release();
    };

    openButton.addEventListener('click', open);

    for (const button of drawer.querySelectorAll('[data-lh-drawer-close]')) {
      button.addEventListener('click', close);
    }

    drawer.addEventListener('close', release);
    drawer.addEventListener('cancel', release);

    // Escape is the browser's to handle, but doing it here first means the
    // close and the clean-up happen in the same turn rather than waiting on a
    // queued event. The browser's own handling then finds it already closed.
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    // A click on the dialog itself is a click on the backdrop: the panel's
    // own children stop it from reaching here.
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) close();
    });

    // The search field inside the drawer opens the theme's own search modal.
    // Two modal dialogs at once is one too many, so this one steps aside.
    drawer.querySelector('[data-lh-drawer-search]')?.addEventListener('click', () => {
      close();
    });

    // Resized into the desktop layout: the drawer's own trigger is gone, so
    // leaving it open would trap the customer behind a backdrop.
    const desktop = window.matchMedia('(min-width: 1050px)');
    desktop.addEventListener('change', (event) => {
      if (event.matches) close();
    });
  }

  /* ------------------------------------------------------- Mega menu */
  const megaItems = header.querySelectorAll('[data-lh-mega-item]');

  /**
   * A compact panel is centred under its own menu item, so one near the edge
   * of the window would hang off it. This measures once, as the panel is about
   * to be shown, and nudges it back inside. The wide mega sheet is centred on
   * the header and never needs it.
   *
   * @param {Element} item
   */
  const keepOnScreen = (item) => {
    const panel = item.querySelector('.lh__mega--compact');
    if (!panel) return;

    // Measured from the item, never from the panel's own rect: the panel is
    // mid-transform while it opens, so reading it back gives the position it
    // is animating through rather than the one it is heading for.
    const margin = 16;
    const width = panel.offsetWidth;
    const item_box = item.getBoundingClientRect();
    const centre = item_box.left + item_box.width / 2;
    const left = centre - width / 2;
    const right = centre + width / 2;

    let shift = 0;
    if (right > window.innerWidth - margin) shift = window.innerWidth - margin - right;
    if (left + shift < margin) shift = margin - left;

    panel.style.setProperty('--lh-shift', Math.round(shift) + 'px');
  };

  for (const item of megaItems) {
    const toggle = item.querySelector('[data-lh-mega-toggle]');
    if (!toggle) continue;

    if (item.querySelector('.lh__mega--compact')) {
      item.addEventListener('pointerenter', () => keepOnScreen(item));
      item.addEventListener('focusin', () => keepOnScreen(item));
    }

    toggle.addEventListener('click', () => {
      keepOnScreen(item);
      const open = item.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Tabbing out of the panel closes it. :focus-within keeps it open while
    // the keyboard is inside, so this only runs once focus has really left.
    item.addEventListener('focusout', (event) => {
      if (item.contains(event.relatedTarget)) return;
      item.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  if (megaItems.length) {
    const closeAll = () => {
      for (const item of megaItems) {
        item.classList.remove('is-open');
        item.querySelector('[data-lh-mega-toggle]')?.setAttribute('aria-expanded', 'false');
      }
    };

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target instanceof Node && header.contains(target)) return;
      closeAll();
    });
  }

  /* --------------------------------------- Only when scrolling up */
  // With "Show it: Only when scrolling up", the header slides away while
  // the page scrolls down and comes back on any scroll up. It never goes
  // while it is in use: hovered, holding keyboard focus, a menu open, or
  // the mobile drawer open. On desktop the theme scrolls .page-wrapper
  // rather than the window, so both are watched.
  if (header.classList.contains('lh--scroll-up')) {
    const section = header.parentElement;
    const pageWrapper = document.querySelector('.page-wrapper');
    const canHover = window.matchMedia('(hover: hover)');
    const STEP = 6;

    const scrollTop = () => Math.max(window.scrollY, pageWrapper ? pageWrapper.scrollTop : 0);

    // Where the header's bottom edge rests on the page: the sections above
    // it in the group (the announcement bar), plus itself.
    const restingBottom = () => {
      let top = 0;
      for (let el = section.previousElementSibling; el; el = el.previousElementSibling) {
        if (el instanceof HTMLElement) top += el.offsetHeight;
      }
      return top + header.offsetHeight;
    };

    const inUse = () =>
      (canHover.matches && header.matches(':hover')) ||
      header.matches(':focus-within') ||
      Boolean(header.querySelector('.is-open')) ||
      document.documentElement.classList.contains('lh-drawer-open');

    let away = false;
    let last = scrollTop();
    let frame = 0;

    const setAway = (next) => {
      if (next === away) return;
      away = next;
      header.classList.toggle('lh--away', away);
      header.dataset.stickyState = away ? 'idle' : 'active';
    };

    const update = () => {
      frame = 0;
      const top = scrollTop();
      const delta = top - last;

      if (top <= restingBottom()) {
        setAway(false);
        last = top;
        return;
      }
      if (Math.abs(delta) < STEP) return;
      setAway(delta > 0 && !inUse());
      last = top;
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    pageWrapper?.addEventListener('scroll', onScroll, { passive: true });
    header.addEventListener('focusin', () => setAway(false));
  }

  /* --------------------------------------------------- Header height */
  const setHeight = () => {
    document.body.style.setProperty('--header-height', `${Math.round(header.offsetHeight)}px`);
  };

  setHeight();

  if ('ResizeObserver' in window) {
    new ResizeObserver(setHeight).observe(header);
  } else {
    window.addEventListener('resize', setHeight);
  }
})();
